import { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import api, { formatPrice, formatDate } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
    AlertDialog,
    AlertDialogContent,
    AlertDialogHeader,
    AlertDialogTitle,
    AlertDialogDescription,
    AlertDialogFooter,
} from "../components/ui/alert-dialog";
import ListingCard from "../components/ListingCard";
import { Plus, Heart, MessageSquare, List, Trash2, Edit, Building2, User, Users, Shield, CreditCard, Package, MapPin, Phone, Mail, Send, Handshake, Settings as SettingsIcon, CheckCircle2, XCircle, TrendingDown, ArrowRight, Repeat, Bell, BellOff, Search as SearchIcon, RefreshCw, Loader2, AlertCircle, Gavel, FileSignature, Sparkles, Clock, FileText, ArrowRightLeft, QrCode, Unlock, Calculator, Zap, Eye, Gift, LifeBuoy, UserCheck, Percent, Compass } from "lucide-react";
import { formatAutoReject } from "../lib/formatAutoReject";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Textarea } from "../components/ui/textarea";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { OfferStatusBadge } from "../components/OfferPanel";
import ListingPerformanceHeatmap from "../components/listing/ListingPerformanceHeatmap";
import { TradeInStatusBadge } from "../components/TradeInPanel";
import useUserSocket from "../lib/useUserSocket";
import useDashboardKpis from "../lib/useDashboardKpis";
import ReauthGate from "../components/ReauthGate";
// Phase 3.24 — PushSettingsSection wurde komplett entfernt (User-Wunsch).
import PushSettingsSection from "../components/PushSettingsSection";
import ReservationsList from "../components/ReservationsList";
import DealerReservationsList from "../components/DealerReservationsList";
import MissedCallsList from "../components/MissedCallsList";
import WorkshopBookings from "../components/WorkshopBookings";
void PushSettingsSection; // Phase 3.24 — UI entfernt, Komponente bleibt für mögliche Backend-Service-Worker-Registrierung erhalten.
import NotificationPermissionBanner from "../components/NotificationPermissionBanner";
import DealerApprovalBanner from "../components/dashboard/DealerApprovalBanner";
import BriefingCard from "../components/dashboard/BriefingCard";
import CallListCard from "../components/dashboard/CallListCard";
import { ReorderReminderBanner } from "../components/dashboard/ReorderReminderBanner";
import LegalReconfirmDialog from "../components/dealer/LegalReconfirmDialog";
import IntranetAnnouncementsBanner from "../components/dashboard/IntranetAnnouncementsBanner";
import TasksHintsTabs from "../components/dashboard/TasksHintsTabs";
import OrgTasksPanel from "../components/dashboard/OrgTasksPanel";
import { restartOnboardingTour } from "../components/OnboardingTour";
import SellerCommissionsCard from "../components/dashboard/SellerCommissionsCard";
import SellerCommissionStatsCard from "../components/dashboard/SellerCommissionStatsCard";
import CrossSellingChampionCard from "../components/dashboard/CrossSellingChampionCard";
import EmployeeTimeCard from "../components/dashboard/EmployeeTimeCard";
import { TimeReminderNudge } from "../components/dashboard/TimeReminderNudge";
import DealerCockpit from "../components/dashboard/DealerCockpit";
import RedPlateExpiryAlert from "../components/dealer/RedPlateExpiryAlert";
import TaxComplianceGapsWidget from "../components/dealer/TaxComplianceGapsWidget";
import OpenTransportsCard from "../components/dashboard/OpenTransportsCard";
import InventoryMarginCard from "../components/dashboard/InventoryMarginCard";
import { MyDrafts, MyFavorites, MySavedSearches } from "../components/dashboard/DashboardSimpleTabs";
import { MyListings, MyMessages, MyOffers, MyTradeIns, DealerBuyRequests } from "../components/dashboard/DashboardTabs";
import Settings from "./DashboardSettings";

export default function DashboardPage() {
    const { user, loading, refresh: refreshAuth } = useAuth();
    const navigate = useNavigate();
    const [params, setParams] = useSearchParams();
    const tab = params.get("tab") || "inbox";
    const { t } = useTranslation();
    // Phase 4g.A — Live-KPIs für Quick-Action-Hover-Tooltips (60 s Refresh).
    const kpis = useDashboardKpis(user);
    // Unsaved-Changes-Warnung: lifted up from Settings → DealerProfileSection/MarketSave
    const [settingsDirty, setSettingsDirty] = useState(false);
    // Phase 3.85 — Ref into Settings so the "leave-without-saving" dialog can
    // trigger Save-everything before navigating away.
    const settingsSaveRef = useRef(null);
    // Pending tab change while the confirm dialog is open.
    const [pendingTabChange, setPendingTabChange] = useState(null);
    // Phase 3.48 — Red-Dot Indikatoren pro Dashboard-Gruppe
    const [groupIndicators, setGroupIndicators] = useState({ messages: 0, selling: 0, buying: 0 });
    // Phase 3.29 — €Punkte-Saldo prominent im Welcome-Banner anzeigen (nur B2B).
    const [pointsTotal, setPointsTotal] = useState(null);
    // Phase 3.29 — Review-Erinnerung für B2C-Buyer (Reservierungen mit can_review).
    const [reviewableReservations, setReviewableReservations] = useState([]);
    const [reviewBannerDismissed, setReviewBannerDismissed] = useState(() => {
        // Phase 3.32 — Persistenz über Reload via localStorage (24h gültig).
        try {
            const raw = localStorage.getItem("tw_review_banner_dismissed_until");
            return raw && Number(raw) > Date.now();
        } catch { return false; }
    });
    // Phase 3.31 — Live Review-Alerts für Händler (negative Bewertungen).
    const [negativeReviewAlert, setNegativeReviewAlert] = useState(() => {
        // Phase 3.32 — Wiederherstellung aus localStorage (gilt 12h).
        try {
            const raw = localStorage.getItem("tw_negative_review_alert");
            if (!raw) return null;
            const obj = JSON.parse(raw);
            if (!obj || !obj.at || Date.now() - obj.at > 12 * 60 * 60 * 1000) return null;
            return obj;
        } catch { return null; }
    });
    // Guard against double-fire of onValueChange (Radix Tabs invokes it twice on some events)
    const tabChangeGuard = useRef({ value: null, at: 0 });

    useEffect(() => {
        if (!loading && (!user || user === false)) navigate("/login");
    }, [user, loading, navigate]);

    // Jul-2026 (User-Wunsch): Neue Händler ohne E-Mail-Bestätigung bzw. ohne
    // Team-Freigabe landen auf der Begrüßungsseite — Eingaben/Einstellungen
    // sind bis zur Freigabe gesperrt, Umsehen ist erlaubt.
    useEffect(() => {
        if (user && user.role === "b2b" &&
            (user.verification_status === "pending" || user.verification_status === "rejected" || user.email_verified === false)) {
            navigate("/willkommen", { replace: true });
        }
    }, [user, navigate]);

    // Stripe return: ?stripe=success after subscription checkout, or
    // ?stripe=cancelled when the dealer aborted. Show a toast, refresh the
    // session (so the new tariff_id + subscription_status are reflected),
    // and strip the params from the URL.
    useEffect(() => {
        const flag = params.get("stripe");
        if (!flag) return;
        if (flag === "success") {
            toast.success("Tarif erfolgreich gebucht — willkommen an Bord!");
            refreshAuth?.();
            setParams((prev) => {
                const next = new URLSearchParams(prev);
                next.delete("stripe");
                next.delete("session_id");
                next.set("tab", "settings");
                return next;
            }, { replace: true });
        } else if (flag === "cancelled") {
            toast.info("Stripe-Checkout abgebrochen — kein Tarif gebucht.");
            setParams((prev) => {
                const next = new URLSearchParams(prev);
                next.delete("stripe");
                return next;
            }, { replace: true });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [params]);

    // Phase 3.48 — Indikatoren regelmäßig abrufen (auch beim Tab-Wechsel),
    // damit der rote Punkt sofort verschwindet, wenn der User in die Gruppe geht.
    useEffect(() => {
        if (!user || !user.id) return;
        let cancelled = false;
        const load = async () => {
            try {
                const r = await api.get("/dashboard/group-indicators");
                if (!cancelled) setGroupIndicators(r.data || { messages: 0, selling: 0, buying: 0 });
            } catch { /* ignore */ }
        };
        load();
        const interval = setInterval(load, 60_000);
        return () => { cancelled = true; clearInterval(interval); };
    }, [user, tab]);

    // Browser-Close / Reload guard
    useEffect(() => {
        if (!settingsDirty) return undefined;
        const handler = (e) => {
            e.preventDefault();
            e.returnValue = "";
            return "";
        };
        window.addEventListener("beforeunload", handler);
        return () => window.removeEventListener("beforeunload", handler);
    }, [settingsDirty]);

    // Phase 3.29 — Lade €Punkte-Saldo für B2B-Banner.
    useEffect(() => {
        let alive = true;
        if (user && user.role === "b2b") {
            api.get("/users/me/points")
                .then((r) => { if (alive) setPointsTotal(r.data?.balance?.total || null); })
                .catch(() => { /* optional */ });
        }
        return () => { alive = false; };
    }, [user]);

    // Phase 3.29 — Lade bewertbare Reservierungen für B2C-Buyer.
    useEffect(() => {
        let alive = true;
        if (user && user.role === "b2c") {
            api.get("/reservations/me")
                .then((r) => {
                    if (!alive) return;
                    const items = r.data?.items || [];
                    setReviewableReservations(items.filter((x) => x.can_review && x.seller?.id));
                })
                .catch(() => { /* optional */ });
        }
        return () => { alive = false; };
    }, [user]);

    // Phase 3.31 — Live Review-Events für Händler. Negative (≤2★) → Toast + Banner.
    useUserSocket(user?.role === "b2b" ? user?.id : null, (evt) => {
        if (!evt) return;
        if (evt.type !== "review_received" && evt.type !== "review_updated") return;
        const rating = Number(evt.rating || 0);
        const reviewer = evt.reviewer_name || "Käufer";
        const isUpdate = evt.type === "review_updated";
        if (evt.is_negative) {
            toast.error(
                `${isUpdate ? "Bewertung aktualisiert" : "Niedrige Bewertung"} (${rating}★) von ${reviewer}`,
                {
                    action: { label: "Antworten", onClick: () => navigate(`/dealer/${user.id}#reviews`) },
                    duration: 12000,
                },
            );
            setNegativeReviewAlert({
                review_id: evt.review_id,
                rating,
                reviewer_name: reviewer,
                title: evt.title || null,
                at: Date.now(),
            });
            // Phase 3.32 — Persistenz über Reload
            try {
                localStorage.setItem("tw_negative_review_alert", JSON.stringify({
                    review_id: evt.review_id,
                    rating,
                    reviewer_name: reviewer,
                    title: evt.title || null,
                    at: Date.now(),
                }));
            } catch { /* ignore quota / privacy mode */ }
        } else if (rating >= 4) {
            toast.success(`${isUpdate ? "Bewertung aktualisiert" : "Neue Bewertung"} (${rating}★) von ${reviewer}`);
        } else {
            toast.info(`Bewertung (${rating}★) von ${reviewer}`);
        }
        // Refresh points balance (dealer just earned €Punkte for the review)
        api.get("/users/me/points")
            .then((r) => setPointsTotal(r.data?.balance?.total || null))
            .catch(() => {});
    });

    const handleTabChange = (next) => {
        // Debounce duplicate fire from Radix Tabs onValueChange
        const now = Date.now();
        if (tabChangeGuard.current.value === next && now - tabChangeGuard.current.at < 500) {
            return;
        }
        tabChangeGuard.current = { value: next, at: now };

        if (settingsDirty && tab === "settings" && next !== "settings") {
            // Phase 3.85 — Open a 3-button confirm dialog (Save / Discard / Cancel)
            // instead of the binary window.confirm.
            setPendingTabChange(next);
            return;
        }
        setParams({ tab: next });
    };

    // Resolve the pending tab-change after the user picks an action in the dialog.
    const finishPendingNavigation = (didSave) => {
        const next = pendingTabChange;
        setPendingTabChange(null);
        if (!next) return;
        if (didSave) {
            // Saved values flow through onDirtyChange → setSettingsDirty(false) on the
            // next render. Pre-emptively flip so the next handleTabChange call doesn't
            // re-prompt against a stale value.
            setSettingsDirty(false);
        } else {
            setSettingsDirty(false);
        }
        setParams({ tab: next });
    };

    // Feb-2026 (User-Wunsch 24 May Iter 6) — SPA-Route-Blocker via useBlocker
    // wurde entfernt, weil useBlocker einen Data-Router benötigt
    // (createBrowserRouter), unsere App jedoch BrowserRouter+<Routes> nutzt.
    // Tab-interne Wechsel sind weiterhin via handleTabChange geschützt;
    // Browser-Close/Reload via beforeunload-Handler (siehe weiter oben).
    // SPA-Link-Navigation aus dem Settings-Tab heraus ist aktuell ungated —
    // ein voller Data-Router-Umbau ist ein eigenständiges Refactoring.
    const navBlocker = null;

    const closeNavBlocker = (_proceed) => {
        // No-op — Stub bleibt für künftige Wiedereinführung.
    };

    // Phase 3.55 — Mobile-Segment: aktive Gruppe ermitteln aus dem aktuellen Tab.
    const groupOfTab = (t) => {
        if (t === "inbox") return "inbox";
        if (t === "messages") return "messages";
        if (["listings", "drafts", "offers", "reservations", "seller-reservations", "open-transports", "test-rides", "missed-calls"].includes(t)) return "selling";
        if (["buy-requests", "favorites", "saved-searches", "tradeins"].includes(t)) return "buying";
        return "inbox";
    };
    const [mobileGroup, setMobileGroup] = useState(() => groupOfTab(tab));
    useEffect(() => {
        // Beim Tab-Wechsel die Mobile-Gruppe synchronisieren.
        const id = setTimeout(() => setMobileGroup(groupOfTab(tab)), 0);
        return () => clearTimeout(id);
    }, [tab]);

    if (loading || !user || user === false) {
        return <div className="max-w-7xl mx-auto px-4 py-12">{t("common.loading")}</div>;
    }

    // Phase 4c++ — Verkäufer-Mitarbeiter sehen ein eingeschränktes Dashboard
    // mit Tabs „Nachrichten" + „Ich biete / verkaufe". Die Listings-Liste
    // wird Backend-seitig automatisch auf `assigned_seller_id` gefiltert.
    const isSellerEmployee = user.role === "zweiradverkaeufer" && user.employee_function === "verkaeufer";
    // Jul-2026 (User-Wunsch): Filial-/Verkaufsleitung sieht das volle Händler-Cockpit
    // der eigenen Filiale (ohne Firmen-Einstellungen).
    const isLeaderEmployee = user.role === "zweiradverkaeufer" &&
        ["filialleiter", "verkaufsleiter"].includes(user.employee_function);
    const canSellDashboard = user.role === "b2b" || isSellerEmployee || isLeaderEmployee;

    // Jul-2026 (User-Wunsch) — freigeschaltete Cockpit-Bereiche des Mitarbeiters.
    // menu_sections_hidden enthält versteckte Keys (cockpit-<area>); alles andere
    // ist sichtbar. Mapping cockpit-Key → Cockpit-Area-ID.
    const COCKPIT_KEY_TO_AREA = {
        "cockpit-verkauf": "verkauf", "cockpit-ankauf": "ankauf", "cockpit-werkstatt": "werkstatt",
        "cockpit-disposition": "disposition", "cockpit-buchhaltung": "rechnungen", "cockpit-system": "system",
    };
    const _hidden = Array.isArray(user.menu_sections_hidden) ? user.menu_sections_hidden : [];
    const employeeCockpitAreas = Object.entries(COCKPIT_KEY_TO_AREA)
        .filter(([key]) => !_hidden.includes(key))
        .map(([, area]) => area);

    return (
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-8 md:py-12" data-testid="dashboard-page">
            <DealerApprovalBanner />
            <LegalReconfirmDialog />
            {/* Jun-2026 (User-Wunsch) — Bestell-Hinweis nur für Filialleitung, Kundendienst + Lagerist; Aug-2026: nicht auf dem Einstellungen-Tab */}
            {tab !== "settings" && (user.role === "b2b" || (user.role === "zweiradverkaeufer" && ["filialleiter", "kundendienst", "lagerist"].includes(user.employee_function))) && <ReorderReminderBanner />}
            {(user.role === "b2b" || user.role === "zweiradverkaeufer") && <IntranetAnnouncementsBanner />}
            {/* Jul-2026 (User-Wunsch) — persönliches KI-Briefing als Begrüßungskarte */}
            {(user.role === "b2b" || user.role === "zweiradverkaeufer") && <BriefingCard user={user} />}
            {/* Aug-2026 (User-Wunsch „Anrufliste Morgens") — Tages-Anrufliste direkt beim Start; nicht auf dem Einstellungen-Tab */}
            {tab !== "settings" && (user.role === "b2b" || user.role === "zweiradverkaeufer") && <CallListCard user={user} />}
            {/* Jul-2026 (User-Wunsch) — kompakter Kopf: Rollen-Bezeichnung, Firmenname,
                Badge, Punkte & Adresse in EINER Karte; „Tour starten" + „Was kann
                TwoWheels?" rechts vom Firmennamen. */}
            <div className="bg-white border border-gray-200 rounded-sm p-4 mb-4" data-testid="dashboard-header-card">
                <div className="flex items-center gap-4 flex-wrap">
                    {(() => {
                        const logo = user.company_logo_url || user.logo_url || user.embed_logo_url || user.profile_photo_url;
                        if (logo) {
                            return (
                                <div className="w-14 h-14 md:w-16 md:h-16 rounded-sm bg-white border border-gray-200 flex items-center justify-center overflow-hidden shrink-0 p-1" data-testid="dashboard-header-logo">
                                    <img src={logo} alt={user.company_name || user.name || "Logo"} className="max-w-full max-h-full object-contain" />
                                </div>
                            );
                        }
                        return (
                            <div className={`w-14 h-14 md:w-16 md:h-16 rounded-sm flex items-center justify-center font-display font-black text-2xl shrink-0 ${user.role === "b2b" ? "bg-[#111827] text-[#FFCC00]" : "bg-[#FFCC00] text-[#111827]"}`} data-testid="dashboard-header-avatar">
                                {(user.company_name || user.name || "?").charAt(0)}
                            </div>
                        );
                    })()}
                    <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                            <h1 className="font-display text-xl md:text-2xl font-black tracking-tight truncate" data-testid="dashboard-name">{user.company_name || user.name}</h1>
                            <Badge className={user.role === "b2b" ? "bg-[#111827] text-white hover:bg-[#111827] uppercase tracking-wide font-bold rounded-sm" : "bg-gray-200 text-gray-800 hover:bg-gray-200 uppercase tracking-wide font-bold rounded-sm"}>
                                {user.role === "b2b" ? "Händler" : isLeaderEmployee ? (user.employee_function === "filialleiter" ? "Filialleitung" : "Verkaufsleitung") : isSellerEmployee ? "Verkäufer · Mitarbeiter" : user.role === "zweiradverkaeufer" ? `Mitarbeiter${user.employee_function ? " · " + user.employee_function : ""}` : "Privat (B2C)"}
                            </Badge>
                            {user.role === "b2b" && pointsTotal && (pointsTotal.points > 0 || pointsTotal.euro > 0) && (
                                <a
                                    href="#dealer-stats-section"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        const el = document.getElementById("dealer-stats-section");
                                        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
                                    }}
                                    className="inline-flex items-center gap-1.5 px-2 py-0.5 bg-[#FFCC00] text-[#111827] rounded-sm hover:bg-[#E6B800] transition-colors"
                                    data-testid="welcome-points-chip"
                                    title="€Punkte-Saldo & Statistik anzeigen"
                                >
                                    <Sparkles className="w-3.5 h-3.5" />
                                    <span className="text-xs font-black font-mono-num tracking-wide">
                                        {pointsTotal.points.toLocaleString("de-DE")} P
                                    </span>
                                    <span className="text-[10px] font-bold opacity-80 font-mono-num">
                                        · {pointsTotal.euro.toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €
                                    </span>
                                </a>
                            )}
                        </div>
                        <div className="text-xs text-gray-500 flex items-center gap-2 flex-wrap mt-0.5" data-testid="dashboard-address-line">
                            <span className="font-bold uppercase tracking-wider text-gray-400" data-testid="dashboard-role-title">
                                {user.role === "b2b" ? "Händler-Dashboard" : isLeaderEmployee ? (user.employee_function === "filialleiter" ? "Filialleiter-Dashboard" : "Verkaufsleiter-Dashboard") : isSellerEmployee ? "Verkäufer-Dashboard" : user.role === "zweiradverkaeufer" ? "Mitarbeiter-Dashboard" : "Privat-Dashboard"}
                            </span>
                            <span className="text-gray-300">·</span>
                            {user.role === "b2b" ? (
                                <>
                                    <span>
                                        {[
                                            [user.street, user.house_number].filter(Boolean).join(" "),
                                            [user.postal_code, user.city].filter(Boolean).join(" "),
                                        ].filter(Boolean).join(", ") || "—"}
                                    </span>
                                    {user.site_code && (
                                        <span className="inline-flex items-center text-[10px] font-bold uppercase tracking-wider text-gray-600 bg-gray-100 border border-gray-200 rounded-sm px-1.5 py-0.5 font-mono-num" data-testid="dashboard-site-code">
                                            {user.site_code}
                                        </span>
                                    )}
                                </>
                            ) : (
                                <span>{user.email}</span>
                            )}
                        </div>
                    </div>
                    {canSellDashboard && (
                        <div className="ml-auto flex items-center gap-2 flex-wrap">
                            <button
                                type="button"
                                onClick={() => restartOnboardingTour(user.role)}
                                data-testid="dashboard-tour-btn"
                                className="inline-flex items-center gap-1.5 px-3 h-9 rounded-sm border-2 border-[#111827] text-[#111827] font-bold text-xs uppercase tracking-wider hover:bg-[#FFCC00] transition-colors"
                                title="Produkt-Tour erneut starten — anpassbares Cockpit, Angebots-Wiedervorlagen & aufgeräumte Navigation"
                            >
                                <Compass className="w-4 h-4" /> Tour starten
                            </button>
                            <Link
                                to="/hilfe"
                                data-testid="dashboard-help-link"
                                className="inline-flex items-center gap-1.5 px-3 h-9 rounded-sm border-2 border-[#111827] text-[#111827] font-bold text-xs uppercase tracking-wider hover:bg-[#FFCC00] transition-colors"
                                title="Was kann TwoWheels? — Funktionen & Hintergrund-Automatik"
                            >
                                <LifeBuoy className="w-4 h-4" /> Was kann TwoWheels?
                            </Link>
                        </div>
                    )}
                </div>
            </div>
            {user.role === "b2b" && <RedPlateExpiryAlert className="mb-4" />}
            {(user.role === "b2b" || isSellerEmployee) && <TaxComplianceGapsWidget className="mb-4" />}

            {/* Jun-2026 Konsolidierung (User-Wunsch) — die obere Schnellzugriff-Leiste
                wurde entfernt. Die 6 globalen Schnellzugriffe (inkl. Live-Badges) sind
                jetzt in die bereichsspezifischen Schnellaktionen im „Mein Cockpit"
                (Seitenende) integriert → ein einziger Aktions-Hub. */}

            {/* Phase 3.31 — Live-Alert für negative Bewertungen (B2B-Händler). */}
            {user.role === "b2b" && negativeReviewAlert && (
                <div
                    className="bg-red-50 border-l-4 border-red-500 rounded-sm p-4 mb-6 flex items-start gap-3"
                    data-testid="negative-review-alert"
                >
                    <div className="shrink-0 w-10 h-10 bg-red-500 text-white rounded-sm flex items-center justify-center font-display font-black text-lg">
                        {negativeReviewAlert.rating}★
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="font-display font-bold text-base text-red-900 mb-0.5">
                            Niedrige Bewertung erhalten
                        </div>
                        <p className="text-sm text-red-800 mb-2">
                            <strong>{negativeReviewAlert.reviewer_name}</strong> hat Ihrem Profil {negativeReviewAlert.rating} Stern{negativeReviewAlert.rating === 1 ? "" : "e"} gegeben
                            {negativeReviewAlert.title ? ` — „${negativeReviewAlert.title}"` : ""}.
                            Reagieren Sie schnell mit einer öffentlichen Antwort, um den Eindruck zu wahren.
                        </p>
                        <Link
                            to={`/dealer/${user.id}#reviews`}
                            className="inline-flex items-center gap-1 text-xs font-bold uppercase tracking-wider bg-red-600 text-white px-3 py-1.5 rounded-sm hover:bg-red-700"
                            data-testid="negative-review-alert-cta"
                        >
                            Jetzt antworten <ArrowRight className="w-3 h-3" />
                        </Link>
                    </div>
                    <button
                        onClick={() => {
                            setNegativeReviewAlert(null);
                            try { localStorage.removeItem("tw_negative_review_alert"); } catch { /* ignore */ }
                        }}
                        className="shrink-0 text-red-600 hover:text-red-800 text-lg leading-none px-1"
                        data-testid="negative-review-alert-dismiss"
                        aria-label="Hinweis ausblenden"
                    >
                        ×
                    </button>
                </div>
            )}

            {/* Phase 3.29 — Review-Erinnerungs-Banner für B2C-Buyer mit offenen Bewertungen. */}
            {user.role === "b2c" && reviewableReservations.length > 0 && !reviewBannerDismissed && (
                <div
                    className="bg-[#FFCC00]/15 border-l-4 border-[#FFCC00] rounded-sm p-4 mb-6 flex items-start gap-3"
                    data-testid="review-reminder-banner"
                >
                    <div className="shrink-0 w-10 h-10 bg-[#FFCC00] rounded-sm flex items-center justify-center">
                        <Sparkles className="w-5 h-5 text-[#111827]" />
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="font-display font-bold text-base text-[#111827] mb-0.5">
                            {reviewableReservations.length === 1
                                ? "Bewerten Sie Ihren Händler"
                                : `Bewerten Sie ${reviewableReservations.length} Händler`}
                        </div>
                        <p className="text-sm text-gray-700 mb-2">
                            Ihre Erfahrung hilft anderen Käufern. Vergeben Sie eine kurze Bewertung für{" "}
                            {reviewableReservations.slice(0, 3).map((r, idx) => (
                                <span key={r.id}>
                                    {idx > 0 && (idx === Math.min(reviewableReservations.length, 3) - 1 ? " und " : ", ")}
                                    <Link
                                        to={`/dealer/${r.seller.id}#reviews`}
                                        className="font-bold underline hover:text-[#111827]"
                                        data-testid={`review-reminder-link-${r.id}`}
                                    >
                                        {r.seller.company_name || r.seller.name}
                                    </Link>
                                </span>
                            ))}
                            {reviewableReservations.length > 3 && ` und ${reviewableReservations.length - 3} weitere`}.
                        </p>
                        <Link
                            to="/dashboard?tab=reservations"
                            className="inline-flex items-center gap-1 text-xs font-bold uppercase tracking-wider text-[#111827] hover:underline"
                            data-testid="review-reminder-cta"
                        >
                            Zu meinen Reservierungen <ArrowRight className="w-3 h-3" />
                        </Link>
                    </div>
                    <button
                        onClick={() => {
                            setReviewBannerDismissed(true);
                            // Phase 3.32 — Wiedersehen erst nach 24h
                            try {
                                localStorage.setItem(
                                    "tw_review_banner_dismissed_until",
                                    String(Date.now() + 24 * 60 * 60 * 1000),
                                );
                            } catch { /* ignore */ }
                        }}
                        className="shrink-0 text-gray-500 hover:text-[#111827] text-lg leading-none px-1"
                        data-testid="review-reminder-dismiss"
                        aria-label="Hinweis ausblenden"
                    >
                        ×
                    </button>
                </div>
            )}

            {/* Jun-2026 (User-Wunsch) — „Meine Aufgaben", „Was zu tun ist" und
                „Mitarbeiter-Auslastung" nebeneinander als Tabs (statt gestapelt). */}
            {(user.role === "zweiradverkaeufer" || user.role === "b2b") && tab !== "settings" && (
                <TasksHintsTabs isB2b={user.role === "b2b"} />
            )}

            {/* Aug-2026 (User-Report Jörg) — komplette Aufgaben-Liste des Standorts
                (inkl. unzugewiesener + Filial-Konto-Aufgaben) auch für Filialleiter/
                Verkaufsleiter und Filial-Konten; Briefing-Chips springen hierher. */}
            {tab !== "settings" && (
                (user.role === "zweiradverkaeufer" && ["filialleiter", "verkaufsleiter", "geschaeftsfuehrer", "gebietsleiter", "inhaber", "chef"].includes((user.employee_function || "").toLowerCase()))
                || (user.role === "b2b" && user.branch_role === "filiale")
            ) && (
                <div className="mb-6" data-testid="branch-org-tasks">
                    <OrgTasksPanel />
                </div>
            )}

            {/* Aug-2026 (User-Wunsch) — Anrufliste nur noch unter „Meine Leads"
                (Doppelung mit „Was ist zu tun" entfernt). */}

            {/* Vorlauf-Übersicht (Jun-2026, User-Wunsch) — eintreffende Bestellfahrzeuge.
                Verschoben ins DealerCockpit (zwischen Org-Cockpit und „Mein Cockpit"). */}

            {/* AVV/Nutzungsbedingungen sind jetzt im kompakten DealerApprovalBanner integriert (Jul-2026, User-Wunsch). */}

            {/* Datenqualität + Vorlauf sind ins Cockpit → Disposition gewandert;
                Potenziale in den Tab „Auswertungen" (Aufgaben & Hinweise) — Jul-2026, User-Wunsch. */}

            {/* Aug-2026 (User-Wunsch) — Kundendienst: Direktzugang zur Werkstatt-Auswertung */}
            {user.role === "zweiradverkaeufer" && user.employee_function === "kundendienst" && (
                <Link to="/dealer/analytics?tab=workshop"
                    className="block mb-6 bg-[#111827] text-white rounded-sm p-4 hover:bg-[#1f2937] transition-colors group"
                    data-testid="kd-analytics-tile">
                    <div className="flex items-center justify-between gap-3">
                        <div className="flex items-center gap-3">
                            <TrendingDown className="w-6 h-6 text-[#FFCC00] rotate-180" />
                            <div>
                                <div className="font-display font-black text-base">Werkstatt-Auswertung</div>
                                <div className="text-xs text-gray-300">Werkstattumsätze, Aufteilung &amp; Monteurleistungen — live einsehen</div>
                            </div>
                        </div>
                        <ArrowRight className="w-5 h-5 text-[#FFCC00] group-hover:translate-x-1 transition-transform" />
                    </div>
                </Link>
            )}

            {/* Item 2 (Jun-2026, User-Wunsch) — Mitarbeiter-Zeiterfassung (Stempeluhr). */}
            {user.role !== "zweiradverkaeufer" && <TimeReminderNudge />}
            {user.role === "zweiradverkaeufer" && (
                <div className="mb-6" data-testid="employee-time-section">
                    <h2 className="font-display font-black text-xl text-[#111827] mb-3 flex items-center gap-2">
                        <Clock className="w-5 h-5 text-emerald-600" /> Meine Zeiterfassung
                    </h2>
                    <EmployeeTimeCard />
                </div>
            )}

            {/* F7 (Jun-2026, User-Wunsch) — Verkäufer-Provisionen-Übersicht + Abruf. */}
            {isSellerEmployee && (
                <div className="mb-6" data-testid="seller-commissions-section">
                    <h2 className="font-display font-black text-xl text-[#111827] mb-3 flex items-center gap-2">
                        <Percent className="w-5 h-5 text-indigo-600" /> Meine Provisionen
                    </h2>
                    <SellerCommissionsCard />
                    <div className="mt-4">
                        <SellerCommissionStatsCard />
                    </div>
                    <div className="mt-4">
                        <CrossSellingChampionCard />
                    </div>
                </div>
            )}

            {/* Cross-Selling-Champion auch für Händler/HQ/Führungskräfte sichtbar (motiviert das Team). */}
            {(user.role === "b2b" || isLeaderEmployee) && (
                <div className="mb-6" data-testid="dealer-champion-section">
                    <CrossSellingChampionCard />
                </div>
            )}

            <Tabs value={tab} onValueChange={handleTabChange} className="w-full">
                {/* Phase 3.55 — Mobile-Segmentleiste (1 / 2 / 3) — auf Desktop ausgeblendet.
                    Jun-2026 Konsolidierung — für B2B entfällt sie, da Verkauf/Käufe ins Cockpit gewandert sind (nur Inbox + Nachrichten bleiben als Tabs). */}
                {user.role !== "b2b" && user.role !== "zweiradverkaeufer" && (
                <div className="md:hidden mb-3" data-testid="mobile-group-segmenter">
                    <div className="grid grid-cols-3 gap-1 bg-white border border-gray-200 rounded-sm p-1">
                        {[
                            { key: "messages", label: "Nachrichten", count: groupIndicators.messages },
                            { key: "selling", label: "Verkauf", count: groupIndicators.selling },
                            { key: "buying", label: "Käufe", count: groupIndicators.buying },
                        ].map((g) => (
                            <button
                                key={g.key}
                                type="button"
                                onClick={() => setMobileGroup(g.key)}
                                className={`relative h-9 text-xs font-bold uppercase tracking-wider rounded-sm transition-colors ${mobileGroup === g.key ? "bg-[#111827] text-[#FFCC00]" : "text-gray-700 hover:bg-gray-50"}`}
                                data-testid={`mobile-group-${g.key}`}
                            >
                                {g.label}
                                {g.count > 0 && (
                                    <span className="absolute top-1 right-1 inline-flex items-center justify-center min-w-[16px] h-4 px-1 bg-red-500 text-white text-[10px] font-mono-num rounded-full">
                                        {g.count}
                                    </span>
                                )}
                            </button>
                        ))}
                    </div>
                </div>
                )}
                {/* Phase 3.47 — User-Wunsch: 3 thematische Gruppen statt flacher Tabs-Liste.
                    Feb-2026 (User-Wunsch 27.05.2026) — die komplette gruppierte Navigation
                    (Smart-Inbox · Nachrichten · Quick-Actions · Transaktions-Code ·
                    Referral-Performance · Gruppen „Ich biete/verkaufe" / „Ich suche/kaufe")
                    wird im Settings-Tab AUSGEBLENDET, damit nur die reine Settings-Sektion
                    sichtbar ist. */}
                {tab !== "settings" && (
                <div className="space-y-3 mb-2" data-testid="dashboard-grouped-nav">
                    {/* Gruppe 0 „⚡ Smart-Inbox / Was zu tun ist" entfernt (· 259):
                        redundant zum Tab „Was zu tun ist" oben in „Aufgaben & Hinweise".
                        Die Händler-Empfehlungs-Box ist dorthin umgezogen. */}

                    {/* === Gruppe 1: Nachrichten ===
                        Jun-2026 (User-Wunsch): Für B2B ENTFERNT, da „Nachrichten"
                        bereits als Tab in „Aufgaben & Hinweise" (TasksHintsTabs →
                        gleiche <MyMessages/>-Komponente) erscheint. Nur Nicht-B2B
                        (Kunden ohne TasksHintsTabs) behalten diesen Zugang. */}
                    {user.role !== "b2b" && user.role !== "zweiradverkaeufer" && !user.employer_dealer_id && (
                    <div className={mobileGroup === "messages" ? "" : "hidden md:block"}>
                        <div className="text-[10px] font-bold uppercase tracking-wider text-gray-500 mb-1.5 px-1 flex items-center gap-2">
                            <span>Nachrichten</span>
                        </div>
                        <div className="flex items-center gap-2 flex-wrap">
                            <TabsList className="inline-flex h-auto gap-1 bg-white border border-gray-200 rounded-sm p-1 flex-wrap">
                                <TabsTrigger value="messages" data-testid="tab-messages"><MessageSquare className="w-4 h-4 mr-1.5" /> {t("tabs.messages")}</TabsTrigger>
                            </TabsList>
                        </div>
                    </div>
                    )}

                    {/* === Gruppe 2: Ich biete / verkaufe (nur Nicht-B2B; B2B nutzt das Cockpit) === */}
                    {user.role !== "b2b" && user.role !== "zweiradverkaeufer" && !user.employer_dealer_id && (
                    <div className={mobileGroup === "selling" ? "" : "hidden md:block"}>
                        <div className="text-[10px] font-bold uppercase tracking-wider text-gray-500 mb-1.5 px-1 flex items-center gap-2">
                            <span>2 · Ich biete / verkaufe</span>
                            {groupIndicators.selling > 0 && (
                                <span className="inline-flex items-center gap-1" data-testid="group-indicator-selling">
                                    <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
                                    <span className="text-[10px] font-mono-num text-red-600">{groupIndicators.selling}</span>
                                </span>
                            )}
                        </div>
                        <TabsList className="inline-flex h-auto gap-1 bg-white border border-gray-200 rounded-sm p-1 flex-wrap">
                            <TabsTrigger value="listings" data-testid="tab-listings"><List className="w-4 h-4 mr-1.5" /> Meine Inserate</TabsTrigger>
                            <TabsTrigger value="drafts" data-testid="tab-drafts"><FileText className="w-4 h-4 mr-1.5" /> Entwürfe</TabsTrigger>
                            <TabsTrigger value="offers" data-testid="tab-offers"><Handshake className="w-4 h-4 mr-1.5" /> Preisvorschläge</TabsTrigger>
                            <TabsTrigger value="reservations" data-testid="tab-reservations"><Clock className="w-4 h-4 mr-1.5" /> Verkäufe / Reservierungen</TabsTrigger>
                        </TabsList>
                    </div>
                    )}

                    {/* === Gruppe 3: Ich suche / kaufe (nur Nicht-B2B; B2B nutzt das Cockpit) === */}
                    {user.role !== "b2b" && user.role !== "zweiradverkaeufer" && !user.employer_dealer_id && (
                    <div className={mobileGroup === "buying" ? "" : "hidden md:block"}>
                        <div className="text-[10px] font-bold uppercase tracking-wider text-gray-500 mb-1.5 px-1 flex items-center gap-2">
                            <span>3 · Ich suche / kaufe</span>
                            {groupIndicators.buying > 0 && (
                                <span className="inline-flex items-center gap-1" data-testid="group-indicator-buying">
                                    <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
                                    <span className="text-[10px] font-mono-num text-red-600">{groupIndicators.buying}</span>
                                </span>
                            )}
                        </div>
                        <TabsList className="inline-flex h-auto gap-1 bg-white border border-gray-200 rounded-sm p-1 flex-wrap">
                            <TabsTrigger value="tradeins" data-testid="tab-tradeins"><Repeat className="w-4 h-4 mr-1.5" /> Inzahlungnahme / Tausch</TabsTrigger>
                            <TabsTrigger value="favorites" data-testid="tab-favorites"><Heart className="w-4 h-4 mr-1.5" /> {t("tabs.favorites")}</TabsTrigger>
                            <TabsTrigger value="saved-searches" data-testid="tab-saved-searches"><SearchIcon className="w-4 h-4 mr-1.5" /> {t("tabs.saved_searches")}</TabsTrigger>
                        </TabsList>
                    </div>
                    )}
                </div>
                )}

                <TabsContent value="inbox" className="mt-6">
                    {/* „Was zu tun ist" + Händler-Empfehlungs-Box sind nach oben in
                        „Aufgaben & Hinweise" (TasksHintsTabs) umgezogen (· 259). */}
                </TabsContent>
                <TabsContent value="listings" className="mt-6">
                    <MyListings userId={user.id} />
                </TabsContent>
                {user?.role === "b2b" && (
                    <TabsContent value="inventory-margin" className="mt-6">
                        <InventoryMarginCard />
                    </TabsContent>
                )}
                <TabsContent value="drafts" className="mt-6">
                    <MyDrafts userId={user.id} />
                </TabsContent>
                <TabsContent value="favorites" className="mt-6">
                    <MyFavorites />
                </TabsContent>
                <TabsContent value="saved-searches" className="mt-6">
                    <MySavedSearches />
                </TabsContent>
                <TabsContent value="messages" className="mt-6">
                    <MyMessages currentUser={user} />
                </TabsContent>
                <TabsContent value="offers" className="mt-6">
                    <MyOffers currentUser={user} />
                </TabsContent>
                <TabsContent value="tradeins" className="mt-6">
                    <MyTradeIns currentUser={user} />
                </TabsContent>
                <TabsContent value="reservations" className="mt-6">
                    <ReservationsList />
                </TabsContent>
                {user.role === "b2b" && (
                    <TabsContent value="seller-reservations" className="mt-6">
                        <DealerReservationsList />
                    </TabsContent>
                )}
                {user.role === "b2b" && (
                    <TabsContent value="missed-calls" className="mt-6">
                        <MissedCallsList />
                    </TabsContent>
                )}
                {user.role === "b2b" && (
                    <TabsContent value="open-transports" className="mt-6">
                        <OpenTransportsCard />
                    </TabsContent>
                )}
                {user.role === "b2b" && (
                    <TabsContent value="buy-requests" className="mt-6">
                        <DealerBuyRequests currentUser={user} />
                    </TabsContent>
                )}
                {/* Feb-2026 (User-Wunsch 27.05.2026) — "Statistik"-Tab entfernt.
                    DealerStats wird jetzt fest am Seitenende unter dem
                    KI-Aktivitäts-Feed dargestellt (siehe unten). */}
                {user.role === "b2b" && user.workshop_online_booking_use_platform && (
                    <TabsContent value="bookings" className="mt-6">
                        <div className="bg-blue-50 border border-blue-300 rounded-sm p-6 text-center" data-testid="bookings-tab-moved">
                            <h2 className="font-display font-bold text-xl mb-2">Werkstatt-Termine wurden zusammengefasst</h2>
                            <p className="text-sm text-gray-700 mb-4">
                                Eingehende Online-Buchungen findest du jetzt direkt in den
                                <strong> Werkstatt-Aufträgen</strong> — dort kannst du Buchungen mit
                                einem Klick in vollwertige Aufträge umwandeln.
                            </p>
                            <Link
                                to="/dealer/workshop-orders?tab=bookings"
                                className="inline-block bg-[#FFCC00] hover:bg-[#E6B800] text-[#111827] font-black px-6 py-2.5 rounded-sm text-sm uppercase tracking-wider"
                                data-testid="bookings-redirect-link"
                            >
                                → Zu den Werkstatt-Aufträgen
                            </Link>
                        </div>
                    </TabsContent>
                )}
                <TabsContent value="settings" className="mt-6">
                    {user?.role === "b2b" ? (
                        <ReauthGate currentUser={user} label="Einstellungen">
                            <Settings currentUser={user} onDirtyChange={setSettingsDirty} saveRef={settingsSaveRef} />
                        </ReauthGate>
                    ) : (
                        <Settings currentUser={user} onDirtyChange={setSettingsDirty} saveRef={settingsSaveRef} />
                    )}
                </TabsContent>
            </Tabs>

            {/* Jun-2026 (User-Wunsch) — Anpassbares „Mein Cockpit" am Seitenende:
                bündelt Transaktions-Code, Referral, Auto-Counter, Gast-Leads,
                KI-Aktivitäts-Feed und Statistik & Performance. Widgets sind per
                Drag & Drop sortier- und ein-/ausblendbar, gespeichert pro Händler. */}
            {tab !== "settings" && user?.role === "b2b" && (
                <DealerCockpit user={user} activeTab={tab} kpis={kpis} onOpenGuestInbox={() => setParams({ tab: "messages", guests: "1" })} />
            )}
            {/* Jul-2026 (User-Wunsch) — Mitarbeiter sehen „Mein Cockpit" mit den
                Bereichen, die ihnen in der Mitarbeiter-Verwaltung freigeschaltet
                wurden (menu_sections_hidden steuert die Sichtbarkeit). */}
            {tab !== "settings" && user?.role === "zweiradverkaeufer" && employeeCockpitAreas.length > 0 && (
                <DealerCockpit user={user} activeTab={tab} kpis={kpis} allowedAreas={employeeCockpitAreas} />
            )}

            {/* Phase 3.85 — 3-button confirm dialog for leaving Settings with unsaved changes */}
            <AlertDialog open={!!pendingTabChange || navBlocker?.state === "blocked"} onOpenChange={(o) => {
                if (!o) {
                    setPendingTabChange(null);
                    if (navBlocker?.state === "blocked") navBlocker.reset();
                }
            }}>
                <AlertDialogContent data-testid="settings-leave-dialog">
                    <AlertDialogHeader>
                        <AlertDialogTitle>Ungespeicherte Änderungen</AlertDialogTitle>
                        <AlertDialogDescription>
                            Sie haben in den Einstellungen Änderungen vorgenommen, die noch nicht gespeichert wurden.
                            Möchten Sie sie speichern, bevor Sie die Seite verlassen?
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter className="flex-col sm:flex-row gap-2">
                        <button
                            type="button"
                            onClick={() => {
                                setPendingTabChange(null);
                                if (navBlocker?.state === "blocked") navBlocker.reset();
                            }}
                            className="h-10 px-4 rounded-sm border border-gray-300 hover:bg-gray-50 font-bold uppercase tracking-wider text-xs"
                            data-testid="settings-leave-cancel"
                        >
                            Abbrechen
                        </button>
                        <button
                            type="button"
                            onClick={() => {
                                if (pendingTabChange) {
                                    finishPendingNavigation(false);
                                } else {
                                    closeNavBlocker(true);
                                }
                            }}
                            className="h-10 px-4 rounded-sm border border-red-300 text-red-700 hover:bg-red-50 font-bold uppercase tracking-wider text-xs"
                            data-testid="settings-leave-discard"
                        >
                            Verlassen ohne Speichern
                        </button>
                        <button
                            type="button"
                            onClick={async () => {
                                if (settingsSaveRef.current) {
                                    await settingsSaveRef.current();
                                }
                                if (pendingTabChange) {
                                    finishPendingNavigation(true);
                                } else {
                                    closeNavBlocker(true);
                                }
                            }}
                            className="h-10 px-4 rounded-sm bg-[#111827] hover:bg-black text-white font-bold uppercase tracking-wider text-xs"
                            data-testid="settings-leave-save"
                        >
                            Speichern & Verlassen
                        </button>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}


